/* eslint-disable max-lines */
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { GridContainer } from "@swiggy-private/dls-web-core/dist/components/GridContainer";
import { CROSS_SELL_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { CrossSellHeaderContainer } from "./CrossSellHeaderContainer";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";

// Components
import { TabContainer } from "@swiggy-private/dls-web-core/dist/components/TabContainer";
import { CrossSellItem } from "./components/CrossSellItem";
import { CrossSellNudge } from "./components/CrossSellNudge";
import { MagicItemsLottieOverlay } from "./components/MagicItemsLottieOverlay";
import { MagicItemsTimerPill } from "./components/MagicItemsTimerPill";

// Constants and Types
import { CROSS_SELL_M2_ALL_ITEMS_ADDED, TEXTS, CROSS_SELL_HEADER_LOTTIE_SHOWN } from "@cart-ui/constants";
import { CrossSellM2CarouselProps } from "./types";
import {
    CROSS_SELL_SHAKE_FREQUENCY,
    SHAKE_ANIMATION_TIMEOUT,
    CROSS_SELL_M2_CAROUSEL_PADDING,
    CROSS_SELL_HEADER_PADDING,
} from "./constants";

// Styles and Hooks
import { Container, ContainerV2, Title } from "./CrossSellM2Carousel.style";
import { useCrossSellCarousel } from "./hooks/useCrossSellCarousel";
import { useMagicItems } from "./hooks/useMagicItems";
import { ITypographyV2 } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { LocalStorage, useEffectOnce } from "@swiggy-private/core-ui";
import { isFreebieOptInItem } from "@cart-ui/helpers/Item";
import { isPopFlow, isToingFlow, useSessionStorage } from "@swiggy-private/checkout-core";
import { TabBarTheme } from "@swiggy-private/dls-web-core/dist/components/TabContainer/types";

const CrossSellM2Carousel = ({
    crossSellM2ItemsList,
    onItemCustomisation,
    addItem,
    onImpressionAddItemCarousel,
    onClickItemDetailsCarousel,
    onImpressionCrossSellM2Item,
    onImpressionCarouselItemDetailsPopup,
    PHNudge,
    onPHNudgeImpression,
    defaultTabSelected,
    onClickChangeTabCarousel,
    crossSellHeader,
    rxId,
    sid,
    allCrossSellSuggestionsAddedText,
    allCrossSellSuggestionsAdded = false,
    onImpressionMagicItemsFtue,
    onImpressionMagicItemsTimer,
    onImpressionMagicItemsEmpty,
    onImpressionMagicItemsUnlockPopup,
    onClickDismissMagicItemsUnlockPopup,
    onClickExploreMenuMagicItems,
    onExploreMenuClick,
    onTimerExpired,
    isSnaccStorefrontV2,
}: CrossSellM2CarouselProps) => {
    const theme = useTheme();

    const {
        currentTabItems,
        sortedItems,
        tabOptions,
        handleAddButtonClick,
        handleItemDetailsClick,
        handleTabChange,
        selectedTab,
        handleImpressionCarouselItemDetailsPopup,
    } = useCrossSellCarousel({
        defaultTabSelected,
        crossSellM2ItemsList,
        onItemCustomisation,
        addItem,
        onClickItemDetailsCarousel,
        onClickChangeTabCarousel,
        onImpressionCarouselItemDetailsPopup,
    });

    const presentationInfo = crossSellHeader?.presentationInfo;

    // Use Magic Items hook for all magic items related state and handlers
    const {
        isWidgetUnlocked,
        showMagicItemsUI,
        showOnlyMessageAndTimer,
        showLottieOverlay,
        isLottieVisible,
        lottieUrl,
        handleMagicDealsLottieComplete,
        handleLottieDOMLoaded,
        handleTimerComplete,
        handleTimerStart,
        handleUrgencyTimeReached,
        shouldPlayLottie,
    } = useMagicItems({
        presentationInfo,
        sid,
        onTimerExpired,
        onImpressionMagicItemsFtue,
        onImpressionMagicItemsTimer,
        onImpressionMagicItemsEmpty,
        allCrossSellSuggestionsAdded,
        allCrossSellSuggestionsAddedText,
    });

    const [shouldCrossSellShake, setCrossSellShakeValue] = useSessionStorage(CROSS_SELL_SHAKE_FREQUENCY, false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isFreebieOptIn = useMemo(() => sortedItems.length > 0 && isFreebieOptInItem(sortedItems[0]), [sortedItems]);
    const [playLottie, setPlayLottie] = React.useState(false);
    const currentRxSessionMapValue = `${rxId || ""}-${sid || ""}`;

    const canPlayLottie = useMemo(() => {
        const storedRxSessionMapValue = LocalStorage.getItem<string>(CROSS_SELL_HEADER_LOTTIE_SHOWN, "");
        const hasPlayedForThisSession = storedRxSessionMapValue === currentRxSessionMapValue;
        return !hasPlayedForThisSession;
    }, [currentRxSessionMapValue]);

    useEffectOnce(() => {
        if (sessionStorage.getItem(CROSS_SELL_M2_ALL_ITEMS_ADDED)) {
            sessionStorage.removeItem(CROSS_SELL_M2_ALL_ITEMS_ADDED);
        }
        if (!isEmpty(PHNudge?.crossSellNudge?.message)) {
            onPHNudgeImpression?.();
        }
        if (!LocalStorage.getItem<string>(CROSS_SELL_HEADER_LOTTIE_SHOWN, "")) {
            LocalStorage.setItem(CROSS_SELL_HEADER_LOTTIE_SHOWN, "");
        }
    });

    useEffect(() => {
        const itemIds = currentTabItems?.cross_sell_suggestions?.map((item) => String(item.menu_item_id)) || [];
        const timerData = presentationInfo?.timer_data;
        const timer = timerData?.remaining_time?.time_in_sec;
        const distanceToUnlock = presentationInfo?.widget_properties_info?.properties?.amount_required_to_unlock;
        const eoShown = isWidgetUnlocked;
        const eoMsg = isWidgetUnlocked ? presentationInfo?.header_data?.title?.text || "" : "";
        onImpressionAddItemCarousel(itemIds, selectedTab, {
            isWidgetUnlocked,
            timer,
            distanceToUnlock,
            eoShown,
            eoMsg,
        });
    }, [
        currentTabItems,
        onImpressionAddItemCarousel,
        selectedTab,
        isWidgetUnlocked,
        presentationInfo?.timer_data,
        presentationInfo?.widget_properties_info?.properties?.amount_required_to_unlock,
        presentationInfo?.header_data?.title?.text,
    ]);

    useEffect(() => {
        if (!playLottie) {
            return;
        }
        const storedRxSessionMapValue = LocalStorage.getItem<string>(CROSS_SELL_HEADER_LOTTIE_SHOWN, "");
        const hasPlayedForThisSession = storedRxSessionMapValue === currentRxSessionMapValue;

        if (hasPlayedForThisSession) {
            setPlayLottie(false);
        }
    }, [currentRxSessionMapValue, playLottie]);

    const renderItems = useMemo(
        () => (
            <CrossSellItem
                sortedItems={sortedItems}
                crossSellM2ItemsList={crossSellM2ItemsList}
                selectedTab={selectedTab}
                onAddButtonClick={handleAddButtonClick}
                onItemDetailsClick={handleItemDetailsClick}
                onImpressionCrossSellM2Item={onImpressionCrossSellM2Item}
                onImpressionCarouselItemDetailsPopup={handleImpressionCarouselItemDetailsPopup}
                isLocked={showMagicItemsUI && !isWidgetUnlocked}
                unlockAmount={presentationInfo?.widget_properties_info?.properties?.amount_required_to_unlock}
                itemPresentationAttributes={
                    showMagicItemsUI ? presentationInfo?.item_presentation_attributes : undefined
                }
                onImpressionMagicItemsUnlockPopup={onImpressionMagicItemsUnlockPopup}
                onClickDismissMagicItemsUnlockPopup={onClickDismissMagicItemsUnlockPopup}
                onClickExploreMenuMagicItems={onClickExploreMenuMagicItems}
                onExploreMenuClick={onExploreMenuClick}
                isMagicItems={showMagicItemsUI}
                isSnaccStorefrontV2={isSnaccStorefrontV2}
            />
        ),
        [
            sortedItems,
            crossSellM2ItemsList,
            selectedTab,
            handleAddButtonClick,
            handleItemDetailsClick,
            onImpressionCrossSellM2Item,
            handleImpressionCarouselItemDetailsPopup,
            showMagicItemsUI,
            isWidgetUnlocked,
            presentationInfo,
            onImpressionMagicItemsUnlockPopup,
            onClickDismissMagicItemsUnlockPopup,
            onClickExploreMenuMagicItems,
            onExploreMenuClick,
            isSnaccStorefrontV2,
        ],
    );

    useEffect(() => {
        timeoutRef.current = setTimeout(() => {
            if (isFreebieOptIn) setCrossSellShakeValue(true);
        }, SHAKE_ANIMATION_TIMEOUT);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isFreebieOptIn, setCrossSellShakeValue]);

    const gridItems = useMemo(() => ({ rows: [{ columns: [renderItems] }] }), [renderItems]);

    const renderV2Content = useCallback(
        () => (
            <GridContainer
                spaceBetweenTitleAndContent="empty"
                interItemSpacing="small"
                items={gridItems}
                clearTitleDefaultMargin
                hasCustomSpacing
                title=""
                background="transparent"
            />
        ),
        [gridItems],
    );

    const getTitleVariant = useCallback(() => {
        if (isPopFlow() && !isEmpty(crossSellHeader?.title)) {
            return "V2.overline.neutral.black";
        }
        return "V2.overline.neutral.bold_spaced";
    }, [crossSellHeader?.title]);

    const titleProps = useMemo(() => {
        if ((!isEmpty(presentationInfo) && !isEmpty(presentationInfo?.header_data)) || showMagicItemsUI) {
            return { text: "" };
        }
        return {
            variant: getTitleVariant() as keyof ITypographyV2,
            color: {
                light: theme.colors.light.text_Low_Emphasis,
                dark: theme.colors.light.text_Low_Emphasis,
            },
            text: crossSellHeader?.title || TEXTS.COMPLETE_YOUR_MEAL_CAPS,
        };
    }, [theme, crossSellHeader, getTitleVariant, presentationInfo, showMagicItemsUI]);

    const subtitleProps = useMemo(() => {
        return !isEmpty(presentationInfo?.header_data)
            ? {}
            : {
                  variant: "V2.body2.neutral.bold" as keyof ITypographyV2,
                  color: {
                      light: theme.colors.light.text_High_Emphasis,
                      dark: theme.colors.dark.text_High_Emphasis,
                  },
                  text: crossSellHeader?.subtitle,
              };
    }, [theme, crossSellHeader, presentationInfo]);

    const isSingleTab = useMemo(() => crossSellM2ItemsList?.length === 1, [crossSellM2ItemsList]);
    const shouldShake = useMemo(() => isFreebieOptIn && !shouldCrossSellShake, [isFreebieOptIn, shouldCrossSellShake]);

    const ContainerComponent = useMemo(() => (isSingleTab ? Container : ContainerV2), [isSingleTab]);

    const spaceBetweenTitleAndContent =
        !isEmpty(titleProps.text) && !isEmpty(subtitleProps.text) ? "xSmall" : "xxSmall";

    const isToingCart = isToingFlow();
    const tabBarTheme = useMemo(() => (isToingCart ? TabBarTheme.ORANGE : TabBarTheme.NEUTRAL), [isToingCart]);

    const onVisibilityChange = useCallback(
        (isVisible) => {
            if (isVisible) {
                if (canPlayLottie) {
                    setPlayLottie(true);
                    LocalStorage.setItem(CROSS_SELL_HEADER_LOTTIE_SHOWN, currentRxSessionMapValue);
                } else {
                    setPlayLottie(false);
                }
            }
        },
        [currentRxSessionMapValue, canPlayLottie],
    );

    // Timer pill component - extracted to MagicItemsTimerPill
    const timerPillComponent = (
        <MagicItemsTimerPill
            presentationInfo={presentationInfo}
            showMagicItemsUI={showMagicItemsUI}
            onTimerComplete={handleTimerComplete}
            onTimerStart={handleTimerStart}
            onUrgencyTimeReached={handleUrgencyTimeReached}
            onTimerExpired={onTimerExpired}
            storageKey={sid}
        />
    );

    // Don't return null if we should show the "all added" message for magic items
    if (isEmpty(crossSellM2ItemsList) && !showOnlyMessageAndTimer) {
        return null;
    }

    if (isSingleTab) {
        const containerPadding = isEmpty(presentationInfo?.header_data)
            ? CROSS_SELL_M2_CAROUSEL_PADDING
            : { ...CROSS_SELL_M2_CAROUSEL_PADDING, top: 8 };

        const headerPadding =
            isPopFlow() && !isEmpty(crossSellHeader?.title) && !isEmpty(crossSellHeader?.subtitle)
                ? CROSS_SELL_HEADER_PADDING
                : { bottom: 0 };

        return (
            <ElementVisibilitySensor senseOnce onVisibilityChange={onVisibilityChange}>
                <ContainerComponent
                    cornerRadius="medium"
                    elevation={false}
                    data-testid={CROSS_SELL_TEST_IDS.CAROUSEL_CONTAINER}
                    {...(isSingleTab ? { shouldShake } : {})}
                    containerBackground={presentationInfo?.background}
                    isHeaderDataPresent={!isEmpty(presentationInfo?.header_data) || showMagicItemsUI}
                >
                    {!isEmpty(presentationInfo) &&
                        (!isEmpty(presentationInfo?.header_data) || showOnlyMessageAndTimer || showMagicItemsUI) && (
                            <CrossSellHeaderContainer
                                presentationInfo={presentationInfo}
                                playLottie={playLottie}
                                canPlayLottie={canPlayLottie}
                                isMagicItems={showMagicItemsUI}
                                isLocked={showMagicItemsUI && !isWidgetUnlocked}
                                onTimerComplete={handleTimerComplete}
                                title={crossSellHeader?.title}
                                allCrossSellSuggestionsAdded={allCrossSellSuggestionsAdded}
                                allCrossSellSuggestionsAddedText={allCrossSellSuggestionsAddedText}
                                timerPillComponent={timerPillComponent}
                            />
                        )}

                    {!showOnlyMessageAndTimer && (
                        <GridContainer
                            title={titleProps.text}
                            titleAccessibility={{
                                role: "heading",
                                ariaLevel: 2,
                            }}
                            titleTextVariant={titleProps.variant}
                            customTitleColor={titleProps.color}
                            subtitle={subtitleProps.text}
                            subtitleTextVariant={subtitleProps.variant}
                            customSubtitleColor={subtitleProps.color}
                            spaceBetweenTitleAndContent={spaceBetweenTitleAndContent}
                            interItemSpacing="small"
                            items={gridItems}
                            cornerRadius="medium"
                            hasCustomSpacing
                            containerPadding={containerPadding}
                            background="transparent"
                            headerPadding={headerPadding}
                        />
                    )}
                    <CrossSellNudge nudge={PHNudge} />

                    <MagicItemsLottieOverlay
                        showOverlay={showLottieOverlay}
                        isWidgetUnlocked={isWidgetUnlocked}
                        isLottieVisible={isLottieVisible}
                        lottieUrl={lottieUrl}
                        onLottieComplete={handleMagicDealsLottieComplete}
                        onDOMLoaded={handleLottieDOMLoaded}
                        shouldPlayLottie={shouldPlayLottie}
                    />
                </ContainerComponent>
            </ElementVisibilitySensor>
        );
    }

    return (
        <>
            <ElementVisibilitySensor senseOnce onVisibilityChange={onVisibilityChange}>
                <ContainerComponent
                    cornerRadius="medium"
                    elevation={false}
                    data-testid={CROSS_SELL_TEST_IDS.CAROUSEL_CONTAINER}
                    containerBackground={presentationInfo?.background}
                    isHeaderDataPresent={!isEmpty(presentationInfo?.header_data) || showMagicItemsUI}
                >
                    {!isEmpty(presentationInfo) && (!isEmpty(presentationInfo?.header_data) || showMagicItemsUI) ? (
                        <CrossSellHeaderContainer
                            presentationInfo={presentationInfo}
                            playLottie={playLottie}
                            canPlayLottie={canPlayLottie}
                            isMagicItems={showMagicItemsUI}
                            isLocked={showMagicItemsUI && !isWidgetUnlocked}
                            onTimerComplete={handleTimerComplete}
                            title={crossSellHeader?.title}
                            allCrossSellSuggestionsAdded={allCrossSellSuggestionsAdded}
                            allCrossSellSuggestionsAddedText={allCrossSellSuggestionsAddedText}
                            timerPillComponent={timerPillComponent}
                        />
                    ) : (
                        <Title
                            asElement="h2"
                            variant={titleProps.variant as keyof ITypographyV2}
                            customColor={titleProps.color}
                        >
                            {titleProps.text}
                        </Title>
                    )}
                    {!showOnlyMessageAndTimer && (
                        <TabContainer
                            tabOptions={tabOptions}
                            onChange={handleTabChange}
                            renderContent={renderV2Content}
                            customWrapperBgColor={{ light: "transparent", dark: "transparent" }}
                            tabBarTheme={tabBarTheme}
                        />
                    )}
                    <CrossSellNudge nudge={PHNudge} />
                </ContainerComponent>
            </ElementVisibilitySensor>
        </>
    );
};

export default memo(CrossSellM2Carousel);
